import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Lock, ArrowRight, AlertCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Il pulsante "Entra come cliente Demo" usa credenziali fisse: esiste solo
// negli ambienti che lo abilitano esplicitamente, mai in produzione.
const SHOW_DEMO_LOGIN = process.env.REACT_APP_DEMO_LOGIN === 'true';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/area-clienti';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    await doLogin(email.trim(), password);
  };

  const doLogin = async (mail: string, pwd: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const profile = await login(mail, pwd);
      const roleHome =
        profile.ruolo === 'admin' ? '/admin'
        : profile.ruolo === 'admin_operatore' ? '/admin'
        : '/area-clienti';
      const target = from === '/area-clienti' ? roleHome : from;
      navigate(target, { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      if (msg === "Account non attivato. Contatta l'amministratore.") {
        setError('Il tuo account non è ancora stato attivato. Contatta l\'amministratore.');
      } else if (err?.response?.status === 403 && typeof msg === 'string') {
        setError(msg);
      } else if (err?.response?.status === 401 || err?.response?.status === 422) {
        setError('Email o password non corretti.');
      } else {
        setError('Errore di connessione. Riprova tra qualche istante.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setEmail('demo@dtksrl.eu');
    setPassword('demo123');
    void doLogin('demo@dtksrl.eu', 'demo123');
  };

  return (
    <div data-testid="login-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-lg mx-auto space-y-4">

          {/* Form principale */}
          <div className="rounded-xl border bg-white p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[hsl(210,40%,93%)] flex items-center justify-center">
                <User className="w-6 h-6 text-[hsl(210,64%,29%)]" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Accedi</h1>
                <p className="text-sm text-muted-foreground">Area riservata clienti DTK</p>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mb-4 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <form className="space-y-4" onSubmit={handleLogin} data-testid="login-form">
              <div>
                <label className="text-xs font-medium mb-1.5 block">Email</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    className="w-full h-10 pl-9 pr-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                    placeholder="La tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    data-testid="login-email"
                    required
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium block">Password</label>
                  <Link
                    to="/password-dimenticata"
                    className="text-xs font-medium text-[hsl(210,64%,29%)] hover:underline"
                    data-testid="login-forgot-password"
                  >
                    Password dimenticata?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password"
                    className="w-full h-10 pl-9 pr-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                    placeholder="La tua password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid="login-password"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                data-testid="login-submit"
                className="w-full h-10 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[hsl(210,64%,24%)] transition disabled:opacity-60"
              >
                {isLoading ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <>Accedi <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>

            {/* Accesso rapido demo: solo dove REACT_APP_DEMO_LOGIN=true (dev, e2e). */}
            {SHOW_DEMO_LOGIN && (
            <div className="mt-5 pt-5 border-t">
              <div className="flex items-center gap-2 mb-2.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Accesso rapido</span>
              </div>
              <button
                type="button"
                onClick={handleDemoLogin}
                disabled={isLoading}
                data-testid="login-demo"
                className="w-full h-10 rounded-lg border-2 border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-amber-100 transition disabled:opacity-60"
              >
                <User className="w-4 h-4" />
                Entra come cliente Demo
              </button>
              <p className="text-[11px] text-muted-foreground mt-2 text-center">
                Account dimostrativo precaricato per esplorare l'area clienti.
              </p>
            </div>
            )}

            <div className="mt-6 pt-4 border-t text-sm text-center text-muted-foreground">
              Non hai ancora un account?{' '}
              <Link to="/registrati" className="font-medium text-[hsl(210,64%,29%)] hover:underline">
                Registrati
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
